import * as Schema from "effect/Schema";

import { NonNegativeInt, PositiveInt, TrimmedNonEmptyString } from "./baseSchemas";

export const BULK_TRANSFER_CHUNK_MAX_BYTES = 256 * 1024;
export const BULK_TRANSFER_DEFAULT_MAX_BYTES = 16 * 1024 * 1024;
export const BULK_TRANSFER_ABSOLUTE_MAX_BYTES = 64 * 1024 * 1024;

const BULK_TRANSFER_ID_MAX_LENGTH = 128;
const BULK_TRANSFER_PATH_MAX_LENGTH = 512;
const BULK_TRANSFER_BASE64URL_MAX_LENGTH = Math.ceil((BULK_TRANSFER_CHUNK_MAX_BYTES * 4) / 3);

export const BulkTransferId = TrimmedNonEmptyString.check(
  Schema.isMaxLength(BULK_TRANSFER_ID_MAX_LENGTH),
  Schema.isPattern(/^[A-Za-z0-9_-]+$/),
);
export type BulkTransferId = typeof BulkTransferId.Type;

export const BulkTransferSha256 = Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/));
export type BulkTransferSha256 = typeof BulkTransferSha256.Type;

export const BulkTransferBase64Url = TrimmedNonEmptyString.check(
  Schema.isMaxLength(BULK_TRANSFER_BASE64URL_MAX_LENGTH),
  Schema.isPattern(/^[A-Za-z0-9_-]+$/),
);

export const BulkTransferChunk = Schema.Struct({
  kind: Schema.Literal("chunk"),
  transferId: BulkTransferId,
  offset: NonNegativeInt,
  bytes: PositiveInt.check(Schema.isLessThanOrEqualTo(BULK_TRANSFER_CHUNK_MAX_BYTES)),
  data: BulkTransferBase64Url,
  sha256: BulkTransferSha256,
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type BulkTransferChunk = typeof BulkTransferChunk.Type;

export const BulkTransferStarted = Schema.Struct({
  kind: Schema.Literal("started"),
  transferId: BulkTransferId,
  totalBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(BULK_TRANSFER_ABSOLUTE_MAX_BYTES)),
  sha256: BulkTransferSha256,
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type BulkTransferStarted = typeof BulkTransferStarted.Type;

export const BulkTransferCompleted = Schema.Struct({
  kind: Schema.Literal("completed"),
  transferId: BulkTransferId,
  totalBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(BULK_TRANSFER_ABSOLUTE_MAX_BYTES)),
  sha256: BulkTransferSha256,
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type BulkTransferCompleted = typeof BulkTransferCompleted.Type;

export const BulkTransferStreamEvent = Schema.Union([
  BulkTransferStarted,
  BulkTransferChunk,
  BulkTransferCompleted,
]);
export type BulkTransferStreamEvent = typeof BulkTransferStreamEvent.Type;

export const BulkTransferReadFileInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  relativePath: TrimmedNonEmptyString.check(Schema.isMaxLength(BULK_TRANSFER_PATH_MAX_LENGTH)),
  maxBytes: Schema.optional(
    PositiveInt.check(Schema.isLessThanOrEqualTo(BULK_TRANSFER_ABSOLUTE_MAX_BYTES)),
  ),
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type BulkTransferReadFileInput = typeof BulkTransferReadFileInput.Type;

export const BulkTransferWriteBeginInput = Schema.Struct({
  transferId: BulkTransferId,
  cwd: TrimmedNonEmptyString,
  relativePath: TrimmedNonEmptyString.check(Schema.isMaxLength(BULK_TRANSFER_PATH_MAX_LENGTH)),
  totalBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(BULK_TRANSFER_ABSOLUTE_MAX_BYTES)),
  sha256: BulkTransferSha256,
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type BulkTransferWriteBeginInput = typeof BulkTransferWriteBeginInput.Type;

export const BulkTransferWriteBeginResult = Schema.Struct({
  transferId: BulkTransferId,
  nextOffset: NonNegativeInt,
  state: Schema.Literals(["open", "completed"]),
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type BulkTransferWriteBeginResult = typeof BulkTransferWriteBeginResult.Type;

export const BulkTransferWriteChunkInput = Schema.Struct({
  transferId: BulkTransferId,
  offset: NonNegativeInt,
  bytes: PositiveInt.check(Schema.isLessThanOrEqualTo(BULK_TRANSFER_CHUNK_MAX_BYTES)),
  data: BulkTransferBase64Url,
  sha256: BulkTransferSha256,
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type BulkTransferWriteChunkInput = typeof BulkTransferWriteChunkInput.Type;

export const BulkTransferWriteChunkResult = Schema.Struct({
  transferId: BulkTransferId,
  nextOffset: NonNegativeInt,
  duplicate: Schema.Boolean,
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type BulkTransferWriteChunkResult = typeof BulkTransferWriteChunkResult.Type;

export const BulkTransferIdInput = Schema.Struct({
  transferId: BulkTransferId,
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type BulkTransferIdInput = typeof BulkTransferIdInput.Type;

export const BulkTransferWriteCommitResult = Schema.Struct({
  transferId: BulkTransferId,
  relativePath: TrimmedNonEmptyString,
  totalBytes: NonNegativeInt,
  sha256: BulkTransferSha256,
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type BulkTransferWriteCommitResult = typeof BulkTransferWriteCommitResult.Type;

export const BulkTransferCancelResult = Schema.Struct({
  transferId: BulkTransferId,
  state: Schema.Literals(["cancelled", "completed"]),
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type BulkTransferCancelResult = typeof BulkTransferCancelResult.Type;

export const BulkTransferErrorCode = Schema.Literals([
  "size_limit_exceeded",
  "integrity_mismatch",
  "offset_mismatch",
  "transfer_not_found",
  "transfer_identity_conflict",
  "path_outside_workspace",
  "symlink_escape",
  "transfer_cancelled",
  "transfer_expired",
  "io_error",
]);
export type BulkTransferErrorCode = typeof BulkTransferErrorCode.Type;

export class BulkTransferError extends Schema.TaggedErrorClass<BulkTransferError>()(
  "BulkTransferError",
  {
    code: BulkTransferErrorCode,
    message: TrimmedNonEmptyString,
    transferId: Schema.NullOr(BulkTransferId),
    retryable: Schema.Boolean,
    cause: Schema.optional(Schema.Defect),
  },
) {}
